import { type FunctionComponent } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useServerVarsContext } from '../../../sharedComponents/ServerVarsContext.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';

import { Button } from 'dibs-elements/exports/Button.tsx';
import { useCurrencyStore } from 'dibs-buyer-layout/exports/currencyStore.ts';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile.jsx';

import { type RelatedQuestionItems_question$data } from '../relatedQuestionItems/__generated__/RelatedQuestionItems_question.graphql.ts';
import { type SellerItems_question$data } from '../sellerItems/__generated__/SellerItems_question.graphql.ts';
import styles from './questionItems.scss';

type Props = {
    items: NonNullable<
        | RelatedQuestionItems_question$data['relatedQuestionItems']
        | SellerItems_question$data['sellerItems']
    >['edges'];
    url: string;
    title: string;
    vertical?: string;
};

export const QuestionItems: FunctionComponent<Props> = ({ items, url, title, vertical }) => {
    const { isMobile } = useServerVarsContext();
    const { currency } = useCurrencyStore();

    if (items?.length) {
        return (
            <div className={styles.wrapper}>
                <div className={styles.divider} />
                <div className={styles.itemsWrapper}>
                    <div className={styles.tilesTitle} data-tn="shop-for-section">
                        {vertical ? (
                            <FormattedMessage
                                id="questionsAnswers.shopFromSeller"
                                defaultMessage="Shop More {vertical} from {title} on 1stDibs"
                                values={{
                                    vertical,
                                    title: (
                                        <Link underline="none" href={url}>
                                            {title}
                                        </Link>
                                    ),
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="questionsAnswers.shopForAll"
                                defaultMessage="Shop for {title} on 1stDibs"
                                values={{
                                    title: (
                                        <Link underline="none" href={url}>
                                            {title}
                                        </Link>
                                    ),
                                }}
                            />
                        )}
                    </div>
                    <div className={styles.tilesContainer}>
                        {items.map((questionItem, index) => {
                            const item = questionItem?.node?.item;
                            if (item) {
                                const { serviceId } = item;
                                return (
                                    <div key={`${index}-${serviceId}`} className={styles.tile}>
                                        <ItemTile
                                            viewer={null}
                                            itemSearch={null}
                                            index={index}
                                            disableLinks
                                            isMobile={isMobile}
                                            item={item}
                                            currency={currency}
                                            showQuickView={false}
                                            showMeasurements={false}
                                            useLoFiLazyLoader={isMobile}
                                            disableBorder
                                            disableContentMargin
                                            hideProductDetails
                                            showMSKUSwatch={false}
                                            imageSize="imageSmall"
                                        />
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>
                    <Button
                        className={styles.viewAllItemsButton}
                        dataTn="questionsAnswers-shopAllItems"
                        href={url}
                        type="secondaryAlt"
                        size="medium"
                    >
                        <FormattedMessage
                            id="questionsAnsers.shopAllItems"
                            defaultMessage="Shop All"
                        />
                    </Button>
                </div>
            </div>
        );
    }
    return null;
};
